import { useCallback, useRef, useState } from "react";

interface GuardedAsyncActionState {
  isLoading: boolean;
  error: string | null;
  lastFetchedAt: number | null;
}

interface RunOptions {
  onErrorMessage?: (error: unknown) => string;
}

/**
 * 非同期処理の重複実行を防ぎつつ、共通の読み込み・エラー状態を管理するフック。
 */
export const useGuardedAsyncAction = () => {
  const [state, setState] = useState<GuardedAsyncActionState>({
    isLoading: false,
    error: null,
    lastFetchedAt: null,
  });
  const isRunningRef = useRef(false);

  const run = useCallback(
    async <T>(action: () => Promise<T>, options?: RunOptions): Promise<T | undefined> => {
      if (isRunningRef.current) {
        return undefined;
      }

      isRunningRef.current = true;
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        const result = await action();
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: null,
          lastFetchedAt: Date.now(),
        }));
        return result;
      } catch (error) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: options?.onErrorMessage ? options.onErrorMessage(error) : "予期しないエラーが発生しました。",
        }));
        return undefined;
      } finally {
        isRunningRef.current = false;
      }
    },
    [],
  );

  const setLastFetchedAt = useCallback((value: number | null) => {
    setState((prev) => ({ ...prev, lastFetchedAt: value }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState((prev) => ({ ...prev, error }));
  }, []);

  return {
    isLoading: state.isLoading,
    error: state.error,
    lastFetchedAt: state.lastFetchedAt,
    run,
    setLastFetchedAt,
    setError,
  };
};
